// Parallel HubSpot submission for the Contact Us / Store Locations page.
// Both pages render the exact same native Shopify
// {% form 'contact', id: 'ContactForm' %} (sections/contact-form.liquid and
// sections/locations.liquid) -- only one of the two is ever present on a
// given page, so sharing one script and one #ContactForm lookup is safe.
//
// Routed through the same-origin form relay (Shopify App Proxy ->
// nella-app-proxy -> Linen Plus Middleware -> HubSpot), not a direct
// client-side call to api.hsforms.com. That direct-call pattern (still
// used by some other integrations pending their own migration) was
// confirmed silently blocked by ad blockers/tracking protection on a real
// visitor's browser -- the relay's request goes to this store's own
// domain instead, which isn't on any tracker blocklist. See
// docs/testing/form-relay-strategy.html for the full writeup.
//
// Same as before: fires a second, parallel submission alongside the
// existing native Shopify submit. The Shopify submission (and its native
// contact-form email) is completely unaffected -- this never calls
// preventDefault() and never throws.
(function () {
  const RELAY_URL = '/apps/nella-app-login/forms/contact_us';
  const FORM_TOKEN_URL = '/apps/nella-app-login/forms-token';

  const formEl = document.getElementById('ContactForm');
  if (!formEl) return;
  if (formEl.dataset.hubspotWired) return;
  formEl.dataset.hubspotWired = 'true';

  // Field lookups use stable ids, not `name` attributes -- three of this
  // form's `name`s are built from translation strings
  // (templates.contact.form.name/phone/comment), which a merchant can edit
  // via Shopify's Translate & Adapt tool without touching any theme file.
  // An id-based lookup keeps working regardless of what that text says.

  // This form has one combined "Name" field, but HubSpot's own UI (contact
  // list, record headers, personalization tokens, most native features) is
  // built around the standard firstname/lastname properties -- a name
  // stored only in a custom property would show every contact as "Unknown
  // Contact" throughout HubSpot. Split the same way HubSpot's own embedded
  // forms split a single "Full Name" field: on the first space. Imperfect
  // for multi-part last names, but consistent with what HubSpot itself does.
  function splitName(fullName) {
    const trimmed = (fullName || '').trim().replace(/\s+/g, ' ');
    if (!trimmed) return { firstName: '', lastName: '' };
    const spaceIndex = trimmed.indexOf(' ');
    if (spaceIndex === -1) return { firstName: trimmed, lastName: '' };
    return { firstName: trimmed.slice(0, spaceIndex), lastName: trimmed.slice(spaceIndex + 1) };
  }

  function buildHubSpotFields() {
    const fields = [];
    const push = (name, value) => {
      const v = (value ?? '').toString().trim();
      if (v) fields.push({ name, value: v });
    };
    const byId = (id) => (document.getElementById(id)?.value || '').toString().trim();
    const departmentEl = formEl.querySelector('[name="contact[department]"]');
    const { firstName, lastName } = splitName(byId('ContactForm-name'));

    push('email', byId('ContactForm-email'));
    push('phone', byId('ContactForm-phone'));
    push('firstname', firstName);
    push('lastname', lastName);
    push('contact_department', (departmentEl?.value || '').toString().trim());
    push('contact_message', byId('ContactForm-body'));
    // Hardcoded, not read from the page -- sections/locations.liquid (the
    // only other section that used to render this same #ContactForm id)
    // is confirmed not referenced by any live template, so this script's
    // only actual active page is Contact Us.
    push('inquiry_source', 'Contact Us');

    return fields;
  }

  // Requires a real page/App-Proxy round trip first -- the relay now
  // rejects any submission without a fresh, single-use token.
  //
  // This native form submits with a real page navigation (no
  // preventDefault() below), which starts tearing down this script's JS
  // context almost immediately. Fetching the token AT submit time raced
  // that navigation: `keepalive` only guarantees an already-sent request's
  // bytes survive unload, it does not keep a `.then()` continuation alive
  // to send a second, dependent request once the first resolves. On a
  // slower connection (e.g. mobile data) the navigation routinely won that
  // race, silently dropping the relay call. Fetching the token up front and
  // refreshing it periodically means submit time only ever needs the one
  // (already in-flight-safe) keepalive request.
  const FORM_TOKEN_REFRESH_MS = 5 * 60 * 1000; // server TTL is 10 min; refresh with margin
  let cachedFormToken = null;

  function fetchFormToken() {
    return fetch(FORM_TOKEN_URL)
      .then((r) => r.json())
      .then((data) => (data && data.token) || null)
      .catch(() => null);
  }

  function refreshFormToken() {
    fetchFormToken().then((token) => { cachedFormToken = token; });
  }

  refreshFormToken();
  setInterval(refreshFormToken, FORM_TOKEN_REFRESH_MS);

  function submitToHubSpot() {
    try {
      const payload = {
        fields: buildHubSpotFields(),
        pageUri: window.location.href,
        pageName: document.title,
      };
      fetch(RELAY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, formToken: cachedFormToken }),
        keepalive: true,
      }).catch((err) => console.warn('HubSpot relay failed (Shopify submission is unaffected):', err));
    } catch (err) {
      console.warn('HubSpot relay failed (Shopify submission is unaffected):', err);
    }
  }

  // Fire on submit, never block it. This form has no custom JS validation
  // (confirmed -- only native HTML5 `required`), so by the time a 'submit'
  // event reaches here the browser has already accepted the input as valid.
  // Fire-and-forget, since this never calls preventDefault() and the
  // native Shopify submission's page navigation proceeds regardless.
  formEl.addEventListener('submit', () => {
    submitToHubSpot();
  });
})();
